import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { registrationsAPI } from './api';
import './Admin.css';

const RegistrationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    status: '',
    notes: '',
  });

  const fetchRegistration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await registrationsAPI.getById(id);
      
      if (response.success && response.data) {
        setRegistration(response.data);
        setEditForm({
          status: response.data.status || 'pending',
          notes: response.data.notes || '',
        });
      } else {
        setError('Không tìm thấy đăng ký');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRegistration();
  }, [fetchRegistration]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await registrationsAPI.update(id, editForm);
      await fetchRegistration();
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      status: registration?.status || 'pending',
      notes: registration?.notes || '',
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await registrationsAPI.delete(id);
      navigate('/admin/registrations');
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { class: 'bg-warning text-dark', text: 'Mới' },
      pending: { class: 'bg-warning text-dark', text: 'Chờ xử lý' },
      contacted: { class: 'bg-info', text: 'Đã liên hệ' },
      in_progress: { class: 'bg-primary', text: 'Đang xử lý' },
      completed: { class: 'bg-success', text: 'Hoàn thành' },
      cancelled: { class: 'bg-danger', text: 'Đã hủy' },
    };
    const badge = badges[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${badge.class} fs-6`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `${parseInt(value).toLocaleString('vi-VN')} đ`;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Đang tải dữ liệu...</p>
      </div>
    );
  }

  if (error && !registration) {
    return (
      <div className="admin-error">
        <i className="fas fa-exclamation-triangle"></i>
        <h3>Có lỗi xảy ra</h3>
        <p>{error}</p>
        <div className="d-flex gap-2 justify-content-center">
          <button className="btn btn-primary" onClick={fetchRegistration}>
            <i className="fas fa-redo me-2"></i>
            Thử lại
          </button>
          <Link to="/admin/registrations" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>
            Quay lại
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-registration-detail">
      {/* Page Header */}
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/admin/registrations">Đăng ký</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Chi tiết #{id}
                </li>
              </ol>
            </nav>
            <h1>
              <i className="fas fa-user-circle me-3"></i>
              {registration?.full_name}
            </h1>
            <p>Chi tiết đăng ký tư vấn điện mặt trời</p>
          </div>
          <div className="header-actions">
            {!isEditing ? (
              <>
                <button 
                  className="btn btn-primary"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fas fa-edit me-2"></i>
                  Chỉnh sửa
                </button>
                <button 
                  className="btn btn-outline-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <i className="fas fa-trash me-2"></i>
                  Xóa
                </button>
              </>
            ) : (
              <>
                <button 
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Lưu
                    </>
                  )}
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <i className="fas fa-times me-2"></i>
                  Hủy
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      <div className="row g-4">
        {/* Main Info Card */}
        <div className="col-lg-8">
          <div className="card detail-card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Thông tin đăng ký
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="info-item">
                    <label>
                      <i className="fas fa-user"></i>
                      Họ và tên
                    </label>
                    <p>{registration?.full_name || '-'}</p>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="info-item">
                    <label>
                      <i className="fas fa-phone"></i>
                      Số điện thoại
                    </label>
                    <p>
                      <a href={`tel:${registration?.phone}`} className="phone-link-large">
                        {registration?.phone || '-'}
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="info-item">
                    <label>
                      <i className="fas fa-map-marker-alt"></i>
                      Tỉnh/Thành phố
                    </label>
                    <p>{registration?.province || '-'}</p>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="info-item">
                    <label>
                      <i className="fas fa-bolt"></i>
                      Loại điện
                    </label>
                    <p>
                      <span className={`badge ${registration?.electric_type === 'business' ? 'bg-primary' : 'bg-secondary'}`}>
                        {registration?.electric_type === 'business' ? 'Kinh doanh' : 'Sinh hoạt'}
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="info-item">
                    <label>
                      <i className="fas fa-money-bill-wave"></i>
                      Tiền điện hàng tháng
                    </label>
                    <p className="text-primary fw-bold">
                      {formatCurrency(registration?.monthly_bill)}
                    </p>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="info-item">
                    <label>
                      <i className="fas fa-bullseye"></i>
                      Mục đích
                    </label>
                    <p>{registration?.purpose || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status & Actions Card */}
        <div className="col-lg-4">
          <div className="card detail-card status-card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-tasks me-2"></i>
                Trạng thái & Ghi chú
              </h5>
            </div>
            <div className="card-body">
              <div className="info-item mb-4">
                <label>
                  <i className="fas fa-flag"></i>
                  Trạng thái hiện tại
                </label>
                {isEditing ? (
                  <select
                    name="status"
                    className="form-select"
                    value={editForm.status}
                    onChange={handleInputChange}
                  >
                    <option value="new">Mới</option>
                    <option value="contacted">Đã liên hệ</option>
                    <option value="in_progress">Đang xử lý</option>
                    <option value="completed">Hoàn thành</option>
                    <option value="cancelled">Đã hủy</option>
                  </select>
                ) : (
                  <p>{getStatusBadge(registration?.status)}</p>
                )}
              </div>

              <div className="info-item mb-4">
                <label>
                  <i className="fas fa-sticky-note"></i>
                  Ghi chú
                </label>
                {isEditing ? (
                  <textarea
                    name="notes"
                    className="form-control"
                    rows="4"
                    value={editForm.notes}
                    onChange={handleInputChange}
                    placeholder="Nhập ghi chú..."
                  ></textarea>
                ) : (
                  <p className="notes-content">
                    {registration?.notes || <em className="text-muted">Chưa có ghi chú</em>}
                  </p>
                )}
              </div>

              <hr />

              <div className="info-item">
                <label>
                  <i className="fas fa-calendar-plus"></i>
                  Ngày đăng ký
                </label>
                <p className="small">{formatDate(registration?.created_at)}</p>
              </div>

              {registration?.updated_at && registration.updated_at !== registration.created_at && (
                <div className="info-item mt-3">
                  <label>
                    <i className="fas fa-calendar-check"></i>
                    Cập nhật lần cuối
                  </label>
                  <p className="small">{formatDate(registration?.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card detail-card mt-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-bolt me-2"></i>
                Thao tác nhanh
              </h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <a 
                  href={`tel:${registration?.phone}`} 
                  className="btn btn-success"
                >
                  <i className="fas fa-phone me-2"></i>
                  Gọi điện
                </a>
                <a 
                  href={`sms:${registration?.phone}`} 
                  className="btn btn-outline-primary"
                >
                  <i className="fas fa-sms me-2"></i>
                  Gửi SMS
                </a>
                <a 
                  href={`https://zalo.me/${registration?.phone}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline-info"
                >
                  <i className="fas fa-comment me-2"></i>
                  Nhắn Zalo
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="mt-4">
        <Link to="/admin/registrations" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          Quay lại danh sách
        </Link>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                  Xác nhận xóa
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Bạn có chắc chắn muốn xóa đăng ký của {registration?.full_name}?</strong></p>
                <p className="text-muted">Hành động này không thể hoàn tác.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  <i className="fas fa-times me-2"></i>
                  Hủy
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Đang xóa...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Xóa
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationDetail;